'use client';

import React from 'react';
import Link from 'next/link';
import { twJoin } from 'tailwind-merge';
import { BundleColorDots } from '@/ui/components/pages/products/components/bundle-color-dots/bundle-color-dots';
import { Product } from '@/ui/components/pages/products/types/Product';
import { ProductBadgeOverlay } from '@/ui/components/product-badge/product-badge-overlay';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { CARD_TARGET_CLASSES } from '@/ui/components/tiles/classic-product-detail/bundle-cards/bundle-card';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { useBundleCardSelection } from './use-bundle-card-selection';

export interface ProductCardProps {
  product: Product;
  currency: string;
}

const WHOLE_UNITS = { maximumFractionDigits: 0, minimumFractionDigits: 0 };

/**
 * One entry of the shop overview. The name is the card's only link and stretches over the
 * whole card, so the colour dots of a set can sit on the image as their own controls
 * instead of being nested in a link.
 */
export function ProductCard({ product, currency }: Readonly<ProductCardProps>) {
  const { localizedLink } = useLocaleLink();
  const { formatCurrency } = useCurrencyFormatter();
  const selection = useBundleCardSelection(product);
  const { image, price, href } = selection;
  // A set without a link would open the home page, so it gets no colour choice to carry.
  const hasColorDots = !!product.bundle && !!href && selection.colorOptions.length > 0;

  return (
    <article className="group relative">
      {/* The image box is its own stacking context; the dots sit outside it to stay above the card link. */}
      <div className="relative">
        <div
          className={twJoin(
            'relative z-0 h-50 rounded-3xl border border-neutral-10 py-4',
            'duration-200 ease-in group-hover:shadow-md md:h-[12rem] lg:h-[24rem]',
          )}
        >
          <div className="absolute bottom-0 left-0 right-0 -z-10 h-1/4 bg-gradient-to-t from-neutral-5 to-transparent opacity-30" />
          <ProductBadgeOverlay badges={selection.badges} className="inset-x-2.5 top-2.5" />
          {image && (
            <OptimizedImage
              image={image}
              width={600}
              height={600}
              className="z-10 h-full w-full object-contain"
              sizes="(max-width: 640px) 90vw, 33vw"
            />
          )}
        </div>
        {hasColorDots && (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center">
            <div className="pointer-events-auto">
              <BundleColorDots
                colors={selection.colorOptions}
                selectedColorName={selection.selectedColorKey}
                onSelect={selection.selectColor}
              />
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1 py-4">
        <h3 className="font-bold leading-[20px] sm:text-md sm:leading-6">
          <Link href={localizedLink(href || '/')} className={CARD_TARGET_CLASSES}>
            {product.name}
          </Link>
        </h3>
        {product.introText && (
          <p className="text-md leading-[20px] text-neutral-60 sm:w-4/5 sm:leading-6">
            {product.introText}
          </p>
        )}
        {selection.priceWithoutDiscount > 0 && currency && (
          <span className="block-inline mr-2 pt-1 text-neutral-60 line-through sm:text-md">
            {formatCurrency(selection.priceWithoutDiscount, { currency, ...WHOLE_UNITS })}
          </span>
        )}
        {price > 0 && currency && (
          <span className="block-inline pt-1 font-bold sm:text-md">
            {formatCurrency(price, { currency, ...WHOLE_UNITS })}
          </span>
        )}
      </div>
    </article>
  );
}
